// The Dashboard's numbers (C-1). The run counters come from the Hub (GET /admin/v1/dashboard/runs: counted in the
// database, so they stay right however many runs there are); with the fixtures (npm test) the same counting is done
// here on the store's runs (api2 src/modules/dashboard/ does it in SQL: change both together). Everything else on the
// Dashboard is read from the stores the portal already has. Counts only: the run log has no applicant data.
import type { z } from 'zod'
import type { DashboardRuns as DashboardRunsSchema } from '@/contract/schemas'
import { effectiveState } from '@/lib/flow'
import { api, apiMode } from '@/lib/hubApi'
import { useFlowStore } from '@/store/flowStore'
import { ANSWER_KINDS, type Alert, type Batch, type CapturedCode, type Lender, type ServiceEndpoint, type WorkflowRun, type WorkflowVersion } from '@/types'

export type DashboardRuns = z.infer<typeof DashboardRunsSchema>
export type RunEnv = DashboardRuns['env']
export const PERIODS = [{ value: '1', label: 'Today' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }] as const

/** YYYY-MM-DD in India time */
const istDay = (t: Date | number) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(t)

/** The same counting as api2's runCounters, on runs held in the browser (fixtures). */
export function runCountersOf(runs: WorkflowRun[], { days, env }: { days: number; env: RunEnv }, now = new Date()): DashboardRuns {
  const dayList = Array.from({ length: days }, (_, i) => istDay(now.getTime() - (days - 1 - i) * 86_400_000))
  const from = `${dayList[0]}T00:00:00+05:30`
  const fromMs = new Date(from).getTime()
  const shown = runs.filter((r) => r.env === env && new Date(r.at).getTime() >= fromMs && new Date(r.at).getTime() <= now.getTime())
  const ms = shown.map((r) => r.ms).sort((a, b) => a - b)
  const avg = (list: number[]) => (list.length ? Math.round(list.reduce((n, x) => n + x, 0) / list.length) : null)
  // percentile_cont(0.95): between the two nearest runs
  const p95 = () => {
    if (!ms.length) return null
    const at = 0.95 * (ms.length - 1)
    const lo = Math.floor(at)
    return Math.round(ms[lo] + (ms[Math.min(lo + 1, ms.length - 1)] - ms[lo]) * (at - lo))
  }
  const byLender = new Map<string, WorkflowRun[]>()
  for (const r of shown) byLender.set(r.lenderSlug, [...(byLender.get(r.lenderSlug) ?? []), r])
  return {
    days,
    env,
    from,
    total: shown.length,
    byAnswer: Object.fromEntries(ANSWER_KINDS.map((a) => [a, shown.filter((r) => r.answer === a).length])) as DashboardRuns['byAnswer'],
    avgMs: avg(ms),
    p95Ms: p95(),
    perDay: dayList.map((day) => {
      const of = shown.filter((r) => istDay(new Date(r.at)) === day)
      return { day, total: of.length, errors: of.filter((r) => r.answer === 'error').length }
    }),
    perLender: Array.from(byLender, ([lenderSlug, list]) => ({ lenderSlug, total: list.length, errors: list.filter((r) => r.answer === 'error').length, avgMs: avg(list.map((r) => r.ms)) }))
      .sort((a, b) => b.total - a.total || a.lenderSlug.localeCompare(b.lenderSlug)),
  }
}

export function loadRunCounters(days: number, env: RunEnv): Promise<DashboardRuns> {
  if (apiMode()) return api<DashboardRuns>('GET', `/dashboard/runs?days=${days}&env=${env}`)
  return Promise.resolve(runCountersOf(useFlowStore.getState().runs, { days, env }))
}

/** The tiles that come from the stores (not from the run log). */
export function storeCounters({ lenders, endpoints, alerts, captured }: { lenders: Lender[]; endpoints: ServiceEndpoint[]; alerts: Alert[]; captured: CapturedCode[] }) {
  const states = endpoints.map((e) => {
    const lender = lenders.find((l) => l.slug === e.lenderSlug)
    return effectiveState(e, lender, lender?.products.find((p) => p.slug === e.productSlug))
  })
  return {
    lenders: { total: lenders.length, active: lenders.filter((l) => l.status === 'active').length, draft: lenders.filter((l) => l.status === 'draft').length, off: lenders.filter((l) => l.status === 'off').length },
    liveApis: lenders.filter((l) => l.liveBatch).length,
    endpoints: { live: states.filter((s) => s === 'live').length, test: states.filter((s) => s === 'test').length, stopped: states.filter((s) => s === 'suspended' || s === 'lender_off' || s === 'product_inactive').length },
    openAlerts: alerts.filter((a) => !a.acknowledgedAt).length,
    newCodes: captured.filter((c) => c.known !== 'known').length,
  }
}

export interface StartStep {
  title: string
  detail: string
  done: boolean
  /** where to do it; null = the Add a lender screen when there is no lender yet */
  to: string
}

/**
 * From an empty Hub to a lender live in production (runbook §3), each step ticked from what the Hub already has.
 * The links go to the first lender that still needs the step.
 */
export function startSteps({ lenders, batches, flows, endpoints }: { lenders: Lender[]; batches: Batch[]; flows: WorkflowVersion[]; endpoints: ServiceEndpoint[] }): StartStep[] {
  const first = lenders[0]?.slug
  const at = (tab: string) => (first ? `/lenders/${first}/${tab}` : '/lenders/new')
  const built = batches.some((b) => ['built', 'live', 'previous'].includes(b.status))
  return [
    { title: 'Add the lender', detail: 'Identity, branding, products and key facts, the UAT and production addresses', done: lenders.length > 0, to: '/lenders/new' },
    { title: 'Ask for the IP whitelist', detail: 'The lender whitelists the server’s IP for UAT and production (tab 1 shows our IPs)', done: lenders.some((l) => l.ipWhitelist.state !== 'requested'), to: at('info') },
    { title: 'Set the Env variables', detail: 'Tab 5: the lender’s keys for UAT and production, kept in the vault, never in the code', done: lenders.some((l) => l.envVars.some((v) => v.uat)), to: at('env') },
    { title: 'Create the APIs', detail: '3.1: one API per lender endpoint; the first batch version is made for you', done: batches.length > 0, to: at('api/apis') },
    { title: 'Write and test the code', detail: '3.3 playground: the handler, real UAT calls, a test event for every outcome, Run all tests', done: batches.some((b) => b.status !== 'draft'), to: at('api/playground') },
    { title: 'Build', detail: '3.2: the image, every test event again inside it; the version is fixed', done: built, to: at('api/batches') },
    { title: 'Workflow and endpoint', detail: '3.4 the workflow on the canvas, Run all paths; 3.5 the service endpoint', done: flows.some((f) => f.status !== 'draft') && endpoints.length > 0, to: at('api/workflows') },
    { title: 'Make live', detail: 'The batch, then the workflow: the production container starts first', done: lenders.some((l) => l.liveBatch), to: at('api/batches') },
    { title: 'Switch the lender on', detail: 'The Active switch on the Lenders list: the web app sees it within the cache time', done: lenders.some((l) => l.status === 'active'), to: '/lenders' },
  ]
}
